import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

const Image = ({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 75,
  sizes = '100vw',
  objectFit = 'cover',
  objectPosition = 'center',
  loading = priority ? 'eager' : 'lazy',
  blurDataURL,
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (priority) {
      const preloadImage = new window.Image()
      preloadImage.src = src
      preloadImage.onload = () => setIsLoaded(true)
      preloadImage.onerror = () => setError(true)
    }
  }, [src, priority])

  const handleLoad = (e) => {
    setIsLoaded(true)
    if (onLoad) onLoad(e)
  }

  const handleError = (e) => {
    setError(true)
    if (onError) onError(e)
  }

  return (
    <div
      className={`relative w-full ${className}`}
      style={{
        aspectRatio: width && height ? `${width}/${height}` : undefined,
      }}
    >
      {!isLoaded && !error && (
        <>
          {blurDataURL ? (
            <img
              src={blurDataURL}
              alt=""
              className="absolute inset-0 w-full h-full object-cover filter blur-sm"
              aria-hidden="true"
            />
          ) : (
            <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-md"></div>
          )}
        </>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-600 text-sm">
          Failed to load image
        </div>
      )}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        className={`w-full h-full ${!isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        style={{
          objectFit,
          objectPosition,
        }}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </div>
  )
}

Image.propTypes = {
  /** The source URL of the image */
  src: PropTypes.string.isRequired,
  /** The alt text for the image */
  alt: PropTypes.string.isRequired,
  /** The width of the image in pixels */
  width: PropTypes.number,
  /** The height of the image in pixels */
  height: PropTypes.number,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Whether the image should be loaded with priority (preload) */
  priority: PropTypes.bool,
  /** The quality of the image (1-100) */
  quality: PropTypes.number,
  /** The sizes attribute for responsive images */
  sizes: PropTypes.string,
  /** How the image should be resized to fit its container */
  objectFit: PropTypes.oneOf([
    'contain',
    'cover',
    'fill',
    'none',
    'scale-down',
  ]),
  /** The position of the image within its container */
  objectPosition: PropTypes.string,
  /** The loading behavior of the image */
  loading: PropTypes.oneOf(['lazy', 'eager']),
  /** A base64-encoded blur placeholder image */
  blurDataURL: PropTypes.string,
  /** Callback function when the image loads */
  onLoad: PropTypes.func,
  /** Callback function when the image fails to load */
  onError: PropTypes.func,
}

export default Image
